<template>
  <PageSideNav
    v-if="isReady"
    :title="mode === 'overview' ? 'Overview Navigation' : 'Overview'"
    v-model:activeItem="activeItem"
    :navigationItems="navigationItems"
    :initiallyOpenGroups="initialOpenGroups"
    @itemClick="handleNavigation"
  />
  <div v-else class="pa-4">
    <div class="text-center">
      <v-progress-circular
        indeterminate
        size="32"
        width="3"
        color="primary"
      ></v-progress-circular>
      <p class="text-body-2 text-medium-emphasis mt-3 mb-0">
        Loading navigation...
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import PageSideNav from './PageSideNav.vue';
  import { useReportNavigation } from '../../composables/useReportNavigation';
  import type { Report } from '../../store';
  import type { NavigationItem } from '../../types/navigation';

  interface Props {
    report: Report;
    mode: 'overview' | 'grade' | 'gateway';
    grade?: string;
    hasScienceOfReading?: boolean;
    currentGatewayReport?: any;
    gradeReport?: any;
  }

  const props = defineProps<Props>();

  // Watch for report prop changes
  watch(
    () => props.report,
    () => {},
    { immediate: true, deep: true },
  );

  const { activeItem, isReady, navigationItems, handleNavigation } =
    useReportNavigation({
      report: props.report,
      mode: props.mode,
      grade: props.grade,
      hasScienceOfReading: props.hasScienceOfReading,
      currentGatewayReport: props.currentGatewayReport,
      gradeReport: props.gradeReport,
    });

  // Compute which groups should be initially open
  const initialOpenGroups = computed(() => {
    const openGroups: string[] = [];

    // Always open reports by grade section by default
    openGroups.push('reports-by-grade');

    // Always open downloads section by default if it exists
    const hasDownloads = navigationItems.value.some(
      (item) => item.value === 'downloads',
    );
    if (hasDownloads) {
      openGroups.push('downloads');
    }

    // Add any items that have defaultOpen set to true
    const addDefaultOpenItems = (items: NavigationItem[]) => {
      items.forEach((item) => {
        if (item.defaultOpen) {
          openGroups.push(item.value);
        }
        // Recursively check children
        if (item.children) {
          addDefaultOpenItems(item.children);
        }
      });
    };

    addDefaultOpenItems(navigationItems.value);

    // Return the list of groups that should be open by default

    return openGroups;
  });
</script>
